import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface Conversation {
  id: string;
  customer_id: string;
  store_id: string;
  last_message: string | null;
  last_message_at: string;
  created_at: string;
  store?: { id: string; name: string; logo_url: string | null };
  customer_profile?: { full_name: string | null; avatar_url: string | null };
  unread_count?: number;
}

export interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  is_read: boolean;
  created_at: string;
}

export const useConversations = (mode: 'customer' | 'vendor', storeId?: string) => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchConversations = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    let query = supabase
      .from('conversations')
      .select('*, store:stores(id, name, logo_url)')
      .order('last_message_at', { ascending: false });

    if (mode === 'customer') {
      query = query.eq('customer_id', user.id);
    } else if (storeId) {
      query = query.eq('store_id', storeId);
    }

    const { data } = await query;
    
    if (data) {
      // Fetch unread counts and customer profiles
      const enriched = await Promise.all(data.map(async (conv: any) => {
        const { count } = await supabase
          .from('messages')
          .select('*', { count: 'exact', head: true })
          .eq('conversation_id', conv.id)
          .eq('is_read', false)
          .neq('sender_id', user.id);

        let customer_profile = null;
        if (mode === 'vendor') {
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name, avatar_url')
            .eq('user_id', conv.customer_id)
            .single();
          customer_profile = profile;
        }

        return { ...conv, unread_count: count || 0, customer_profile };
      }));
      setConversations(enriched);
    }
    setLoading(false);
  }, [user, mode, storeId]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Realtime subscription for conversation updates
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('conversations-updates')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'conversations' }, () => {
        fetchConversations();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user, fetchConversations]);

  return { conversations, loading, refetch: fetchConversations };
};

export const useMessages = (conversationId: string | null) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchMessages = useCallback(async () => {
    if (!conversationId) return;
    setLoading(true);
    const { data } = await supabase
      .from('messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });

    if (data) setMessages(data);
    setLoading(false);

    // Mark unread messages as read
    if (user) {
      await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('conversation_id', conversationId)
        .eq('is_read', false)
        .neq('sender_id', user.id);
    }
  }, [conversationId, user]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Realtime for new messages
  useEffect(() => {
    if (!conversationId) return;
    const channel = supabase
      .channel(`messages-${conversationId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `conversation_id=eq.${conversationId}`,
      }, (payload) => {
        const newMsg = payload.new as Message;
        setMessages(prev => [...prev, newMsg]);
        // Mark as read if not sender
        if (user && newMsg.sender_id !== user.id) {
          supabase.from('messages').update({ is_read: true }).eq('id', newMsg.id).then();
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [conversationId, user]);

  const sendMessage = async (content: string) => {
    if (!conversationId || !user || !content.trim()) return;
    await supabase.from('messages').insert({
      conversation_id: conversationId,
      sender_id: user.id,
      content: content.trim(),
    });
    // Update conversation last message
    await supabase.from('conversations').update({
      last_message: content.trim(),
      last_message_at: new Date().toISOString(),
    }).eq('id', conversationId);
  };

  return { messages, loading, sendMessage };
};

export const useStartConversation = () => {
  const { user } = useAuth();

  const startOrGetConversation = async (storeId: string): Promise<string | null> => {
    if (!user) return null;

    // Check existing conversation
    const { data: existing } = await supabase
      .from('conversations')
      .select('id')
      .eq('customer_id', user.id)
      .eq('store_id', storeId)
      .maybeSingle();

    if (existing) return existing.id;

    // Create new
    const { data: created } = await supabase
      .from('conversations')
      .insert({ customer_id: user.id, store_id: storeId })
      .select('id')
      .single();

    return created?.id || null;
  };

  return { startOrGetConversation };
};

export const useUnreadCount = () => {
  const { user } = useAuth();
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!user) return;
    
    const fetchCount = async () => {
      // Get all conversations where user is participant
      const { data: convs } = await supabase
        .from('conversations')
        .select('id');

      if (!convs?.length) return;

      const { count: unread } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .in('conversation_id', convs.map(c => c.id))
        .eq('is_read', false)
        .neq('sender_id', user.id);

      setCount(unread || 0);
    };

    fetchCount();

    const channel = supabase
      .channel('unread-counter')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, () => {
        fetchCount();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user]);

  return count;
};
